import { useMutation } from '@tanstack/react-query';
import { Alert, Card, Flex, Skeleton, Tooltip } from 'antd';
import { ErrorDisplay, ErrorListDisplay } from 'components/ErrorDisplay';
import { Text, Title } from 'components/mvp-typography';
import { processActivateAssessment } from 'lib/api/firebase/apis/apis';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';
import { useErrorList } from 'lib/utils/useErrorList';
import { CreditCard, PaymentForm } from 'react-square-web-payments-sdk';

import { DollarCircleOutlined } from '@ant-design/icons';
import { useGetClinicProductOptions } from 'lib/hooks/tanstack/use-getClinicProductOptions';
import { formatCurrency } from 'lib/utils/formatters/normalise';

export function PayForAssessmentWithWrapper(props: {
  patientId: string;
  clinicId: string | undefined;
  onSuccess?: () => void;
}) {
  if (!props.clinicId) {
    return (
      <Alert
        type="error"
        message={`Test-Taker (id=${props.patientId}) does not have an assigned clinic`}
      />
    );
  }

  return (
    <PayForAssessment clinicId={props.clinicId} onSuccess={props.onSuccess} />
  );
}

export function PayForAssessment(props: {
  clinicId: string;
  onSuccess?: () => void;
}) {
  const errorList = useErrorList();
  const paymentMutation = useMutation({
    mutationFn: (token: string) =>
      processActivateAssessment(token, props.clinicId),
    onError: error => errorList.addError(resolveError(error), 'mutation'),
    onSuccess() {
      // TODO: Trigger PatientInfo refetch with query keys
      props.onSuccess?.();
    }
  });
  const productOptions = useGetClinicProductOptions(props.clinicId);

  if (productOptions.isLoading) {
    return <Skeleton active />;
  }

  if (productOptions.error) {
    return (
      <ErrorDisplay
        title="Failed to retrieve Clinic payment data"
        description="Please report this behaviour to your Clinic."
        error={resolveError(productOptions.error)}
      />
    );
  }

  const option = productOptions.data?.assessment;
  if (!option) {
    return <Text>This Clinic does not support payment with SquareUp.</Text>;
  }

  return (
    <div>
      <ErrorListDisplay errorList={errorList} closeable />

      <Flex gap={16} align="center" vertical>
        <Title level={2}>Complete payment</Title>

        <PurchasePriceDisplay
          amount={option.amount}
          currency={option.currency}
          description={option.description}
        />
        <div
          style={{
            width: '60%',
            maxWidth: 600
          }}
        >
          <Text>
            Your credit card will be charged{' '}
            {formatCurrency(option.currency, option.amount)}.
          </Text>
          <div>
            <PaymentForm
              applicationId={process.env.REACT_APP_SQUARE_APP_ID ?? ''}
              locationId={process.env.REACT_APP_SQUARE_APP_LOCATION ?? ''}
              cardTokenizeResponseReceived={async tokenResult => {
                errorList.reset();

                if (tokenResult.status === 'OK') {
                  paymentMutation.mutate(tokenResult.token);
                }

                // Raise and display an error when the status is not OK
                if (tokenResult.status === 'Error') {
                  tokenResult.errors.forEach(_ =>
                    errorList.addError(resolveError(_))
                  );
                }
              }}
            >
              <CreditCard />
            </PaymentForm>
          </div>
        </div>
        <div>
          {paymentMutation.isSuccess && (
            <Text>
              Request to purchase an assessment has been completed. Rechecking
              assessment status...
            </Text>
          )}
          {paymentMutation.isLoading && <Text>Processing request.</Text>}
          {paymentMutation.isError && (
            <Text>
              Payment request failed. Your account has not been charged.
            </Text>
          )}
        </div>
      </Flex>
    </div>
  );
}

interface PurchasePriceDisplayProps {
  /**
   * Amount in the smallest currency unit (e.g., cents).
   */
  amount: number;
  description?: string | undefined | null;
  /**
   * ISO currency code
   */
  currency: 'USD' | 'CAD';
  /**
   * Optional additional styling.
   */
  className?: string;
  selected?: boolean;
  onSelect?: () => void;
}

function PurchasePriceDisplay(props: PurchasePriceDisplayProps) {
  // Format the number as a localized currency string.
  const formatted = formatCurrency(props.currency, props.amount);

  const cardStyle: React.CSSProperties = {
    width: 250,
    textAlign: 'center',
    cursor: 'pointer',
    border: props.selected ? '2px solid #A366FC' : undefined
  };

  return (
    <Card
      className={props.className}
      style={cardStyle}
      onClick={() => props.onSelect?.()}
    >
      <Tooltip title="">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 8
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8
            }}
          >
            <DollarCircleOutlined style={{ fontSize: 24, color: '#A366FC' }} />
            <Text style={{ fontWeight: 'bold', fontSize: 18 }}>
              {formatted}
            </Text>
          </div>
          <div>
            <Text>{props.description}</Text>
          </div>
        </div>
      </Tooltip>
    </Card>
  );
}
